import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Icon,
  IconButton,
  Link,
  Persona,
  PersonaSize,
  TextField,
  Toggle,
  TooltipHost,
} from "@fluentui/react";
import { mergeStyleSets } from "@uifabric/merge-styles";
import { toast } from "components/FluentToast";
import { FormSpinner } from "components/FormSpinner";
import { Flex, NoData, StyledPrimaryButton } from "components/styled";
import { useRemoveAttachment } from "hooks/data/mutations/useRemoveAttachment";
import { useUpdateAttachment } from "hooks/data/mutations/useUpdateAttachment";
import usePhraseActivationStatus from "hooks/usePhraseActivationStatus";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { Attachment } from "types/types";
import { formatDateString, formatDateStringTimeOnly } from "utils/utils";
import { useSelector } from "../../store/hooks";
import UploadAttachmentDialog, {
  ATTACHMENT_COMMENT_MAX_LENGTH,
} from "./UploadAttachmentDialog";

// same look as the fleet default settings tables: compact, every cell centered, narrow user column
const getClassNames = (theme: IStyledTheme) =>
  mergeStyleSets({
    toolbar: {
      margin: "15px 0",
    },
    table: {
      borderCollapse: "collapse",
      borderTop: `1px solid ${theme.palette.neutralLighterAlt}`,
      width: "100%",
      "& td": {
        padding: "5px 8px",
        fontSize: "12px",
        color: theme.palette.neutralPrimary,
        textAlign: "center",
      },
      "& th": {
        padding: "5px",
        fontSize: "12px",
        fontWeight: "650",
        borderBottom: `1px solid ${theme.palette.neutralLighterAlt}`,
        height: "42px",
        color: theme.palette.neutralPrimary,
      },
    },
    dateCell: {
      fontSize: "12px",
      fontStyle: "italic",
      color: theme.palette.neutralSecondaryAlt,
    },
    deleteIcon: {
      color: theme.palette.redDark,
    },
    commentCell: {
      display: "inline-block",
      maxWidth: "250px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      verticalAlign: "middle",
    },
  });

const formatSize = (sizeBytes: number) => {
  if (sizeBytes >= 1024 * 1024)
    return (sizeBytes / (1024 * 1024)).toFixed(2) + " MB";
  if (sizeBytes >= 1024) return (sizeBytes / 1024).toFixed(1) + " KB";
  return sizeBytes + " B";
};

type Props = {
  fleetId: number;
  vehicleId: number | null; // null = fleet level documents
  attachments: Attachment[] | null | undefined;
  // next = the new list; changed = the attachment as returned by the create/update/remove endpoint, which also carries
  // the vehicle's new row version (a vehicle document change creates a new vehicle version)
  onAttachmentsChange: (next: Attachment[], changed: Attachment) => void;
  canEdit: boolean;
};

// Documents of a fleet or of a vehicle: list with download links; internal users (canEdit) upload/remove them
// and decide whether the client may see them. External users only get the visible ones from the backend.
const AttachmentsPanel = ({
  fleetId,
  vehicleId,
  attachments: attachmentsProp,
  onAttachmentsChange,
  canEdit,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme() as IStyledTheme;
  const classes = getClassNames(theme);
  const taxonomy = useSelector((s) => s.taxonomy);
  const phraseActivated = usePhraseActivationStatus().activated;
  // clients never see the fleet details, so the visibility flag only exists for vehicle documents
  const hasVisibilityColumn = vehicleId != null;

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] =
    useState<Attachment | null>(null);

  const { mutate: onUpdateAttachment, isLoading: isUpdating } =
    useUpdateAttachment();
  const { mutate: onRemoveAttachment, isLoading: isRemoving } =
    useRemoveAttachment();

  const attachments = attachmentsProp || [];

  const [attachmentToComment, setAttachmentToComment] =
    useState<Attachment | null>(null);
  const [commentDraft, setCommentDraft] = useState("");

  // the update endpoint takes the visibility flag and the comment together; whichever changes, the other one is resent as is
  const updateAttachment = (
    attachment: Attachment,
    changes: {
      isVisibleForClient?: boolean;
      attachmentComment?: string | null;
    },
    onDone?: () => void,
  ) => {
    onUpdateAttachment(
      {
        fleetId,
        body: {
          attachmentId: attachment.attachmentId,
          isVisibleForClient:
            changes.isVisibleForClient ?? attachment.isVisibleForClient,
          attachmentComment:
            changes.attachmentComment === undefined
              ? attachment.attachmentComment
              : changes.attachmentComment,
          rowVersion: attachment.rowVersion,
        },
      },
      {
        onSuccess: (res) => {
          onAttachmentsChange(
            attachments.map((a) =>
              a.attachmentId === attachment.attachmentId
                ? { ...a, ...res.data, url: a.url }
                : a,
            ),
            res.data,
          );
          onDone?.();
        },
      },
    );
  };

  const toggleVisibility = (attachment: Attachment, checked: boolean) =>
    updateAttachment(attachment, { isVisibleForClient: checked });

  const openCommentDialog = (attachment: Attachment) => {
    setCommentDraft(attachment.attachmentComment ?? "");
    setAttachmentToComment(attachment);
  };

  const saveComment = () => {
    if (!attachmentToComment) return;
    updateAttachment(
      attachmentToComment,
      { attachmentComment: commentDraft.trim() || null },
      () => {
        toast.success(t("bfm.success"));
        setAttachmentToComment(null);
      },
    );
  };

  const removeAttachment = () => {
    if (!attachmentToDelete) return;
    onRemoveAttachment(
      {
        fleetId,
        attachmentId: attachmentToDelete.attachmentId,
      },
      {
        onSuccess: (res) => {
          toast.success(t("bfm.success"));
          onAttachmentsChange(
            attachments.filter(
              (a) => a.attachmentId !== attachmentToDelete.attachmentId,
            ),
            res.data,
          );
          setAttachmentToDelete(null);
        },
      },
    );
  };

  return (
    <>
      {canEdit && (
        <div className={classes.toolbar}>
          <StyledPrimaryButton
            text={t("bfm.attachments.upload.label")}
            iconProps={{ iconName: "Upload" }}
            onClick={() => setUploadDialogOpen(true)}
          />
        </div>
      )}
      {attachments.length === 0 ? (
        <NoData>{t("bfm.attachments.noData")}</NoData>
      ) : (
        <table className={classes.table}>
          <thead>
            <tr>
              <th style={{ width: "60px" }}>{t("bfm.user")}</th>
              <th>{t("bfm.attachments.type.label")}</th>
              <th>{t("bfm.attachments.name.label")}</th>
              <th>{t("bfm.attachments.size.label")}</th>
              <th>{t("bfm.comment.label")}</th>
              {hasVisibilityColumn && (
                <th>{t("bfm.attachments.visibleForClient.label")}</th>
              )}
              <th>{t("bfm.date.label")}</th>
              {canEdit && <th style={{ width: "80px" }}></th>}
            </tr>
          </thead>
          <tbody>
            {attachments.map((attachment) => (
              <tr
                key={attachment.attachmentId}
                style={{ borderBottom: "1px solid rgb(237, 235, 233)" }}
              >
                <td>
                  <Flex justify="center">
                    <Persona
                      text={attachment.createdByUserName || "System"}
                      title={`${attachment.createdByUserName || "System"} ${
                        attachment.createdByUserEmail
                          ? `(${attachment.createdByUserEmail})`
                          : ""
                      }`}
                      hidePersonaDetails
                      size={PersonaSize.size32}
                    />
                  </Flex>
                </td>
                <td>
                  {t(
                    taxonomy.AttachmentType.byId[attachment.attachmentTypeCode]
                      ?.code,
                  )}
                </td>
                <td>
                  {attachment.url ? (
                    <Link
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t("bfm.attachments.download.label")}
                    >
                      <Icon iconName="Download" style={{ marginRight: 5 }} />
                      {attachment.attachmentName}
                    </Link>
                  ) : (
                    attachment.attachmentName
                  )}
                </td>
                <td>{formatSize(attachment.sizeBytes)}</td>
                <td>
                  <Flex gap={5} justify="center">
                    <TooltipHost content={attachment.attachmentComment ?? ""}>
                      <span className={classes.commentCell}>
                        {attachment.attachmentComment}
                      </span>
                    </TooltipHost>
                    {canEdit && (
                      <IconButton
                        iconProps={{ iconName: "Edit" }}
                        title={t("bfm.edit")}
                        disabled={isUpdating}
                        onClick={() => openCommentDialog(attachment)}
                      />
                    )}
                  </Flex>
                </td>
                {hasVisibilityColumn && (
                  <td>
                    {canEdit ? (
                      <Flex justify="center">
                        <Toggle
                          checked={attachment.isVisibleForClient}
                          onText={t("greco.yes")}
                          offText={t("greco.no")}
                          disabled={isUpdating}
                          styles={{ root: { marginBottom: 0 } }}
                          onChange={(_ev, checked) =>
                            toggleVisibility(attachment, !!checked)
                          }
                        />
                      </Flex>
                    ) : attachment.isVisibleForClient ? (
                      t("greco.yes")
                    ) : (
                      t("greco.no")
                    )}
                  </td>
                )}
                <td>
                  <div className={classes.dateCell}>
                    {formatDateString(attachment.createdAt)}
                  </div>
                  <div className={classes.dateCell}>
                    {formatDateStringTimeOnly(attachment.createdAt)}
                  </div>
                </td>
                {canEdit && (
                  <td>
                    <TooltipHost content={t("bfm.attachments.delete.label")}>
                      <IconButton
                        iconProps={{ iconName: "Delete" }}
                        className={classes.deleteIcon}
                        disabled={isRemoving}
                        onClick={() => setAttachmentToDelete(attachment)}
                      />
                    </TooltipHost>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {uploadDialogOpen && (
        <UploadAttachmentDialog
          fleetId={fleetId}
          vehicleId={vehicleId}
          onUploaded={(attachment) =>
            onAttachmentsChange([...attachments, attachment], attachment)
          }
          close={() => setUploadDialogOpen(false)}
        />
      )}

      {attachmentToComment && (
        <Dialog
          hidden={false}
          onDismiss={() => setAttachmentToComment(null)}
          modalProps={{
            isBlocking: phraseActivated ? false : true,
          }}
          dialogContentProps={{
            type: DialogType.normal,
            title: t("bfm.comment.label"),
            subText: attachmentToComment.attachmentName,
            showCloseButton: true,
          }}
          minWidth={450}
        >
          <TextField
            multiline
            rows={4}
            maxLength={ATTACHMENT_COMMENT_MAX_LENGTH}
            value={commentDraft}
            onChange={(_ev, value) => setCommentDraft(value ?? "")}
          />
          <DialogFooter>
            <StyledPrimaryButton
              text={t("greco.save")}
              disabled={isUpdating}
              onClick={saveComment}
            />
            <DefaultButton
              onClick={() => setAttachmentToComment(null)}
              text={t("bfm.close.label")}
            />
          </DialogFooter>
          {isUpdating && <FormSpinner />}
        </Dialog>
      )}

      {attachmentToDelete && (
        <Dialog
          hidden={false}
          onDismiss={() => setAttachmentToDelete(null)}
          modalProps={{
            isBlocking: phraseActivated ? false : true,
          }}
          dialogContentProps={{
            type: DialogType.normal,
            title: (
              <div style={{ display: "flex", alignItems: "center" }}>
                <Icon
                  iconName="Warning"
                  style={{
                    color: theme.semanticColors.errorIcon,
                    fontSize: "25px",
                    marginRight: "5px",
                  }}
                />
                {t("bfm.attachments.delete.label")}
              </div>
            ),
            subText: t("bfm.attachments.deleteConfirm.label", {
              name: attachmentToDelete.attachmentName,
            }),
          }}
        >
          <DialogFooter>
            <StyledPrimaryButton
              text={t("greco.delete")}
              disabled={isRemoving}
              onClick={removeAttachment}
            />
            <DefaultButton
              onClick={() => setAttachmentToDelete(null)}
              text={t("bfm.close.label")}
            />
          </DialogFooter>
          {isRemoving && <FormSpinner />}
        </Dialog>
      )}
    </>
  );
};

export default AttachmentsPanel;
